import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import { Bell } from 'lucide-react';
import { DetailedItem } from '../../../../../../ui';
import { useEditableForm } from '../hooks/useEditableForm';
import { SettingsCard } from './SettingsCard';

interface NotificationSettingsData {
  emailNotifications: boolean;
  smsNotifications: boolean;
  assessmentReminders: boolean;
  candidateUpdates: boolean;
  systemAlerts: boolean;
  weeklyReports: boolean;
  notificationFrequency: 'immediate' | 'daily' | 'weekly';
  quietHoursStart: string;
  quietHoursEnd: string;
}

interface INotificationSettings {
  onSave?: (data: NotificationSettingsData) => Promise<void> | void;
}

export const NotificationSettings: React.FC<INotificationSettings> = ({ onSave }) => {
  const initialData: NotificationSettingsData = {
    emailNotifications: true,
    smsNotifications: false,
    assessmentReminders: true,
    candidateUpdates: true,
    systemAlerts: true,
    weeklyReports: true,
    notificationFrequency: 'daily',
    quietHoursStart: '22:00',
    quietHoursEnd: '08:00',
  };

  const { isEditing, formData, isSaving, handleEditToggle, handleSave, handleInputChange } = useEditableForm({
    initialData,
    onSave: async (data) => {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (onSave) {
        await onSave(data);
      }
    },
  });

  const handleToggleChange = (field: keyof NotificationSettingsData) => {
    handleInputChange(field, !formData[field]);
  };

  const getFrequencyDisplayText = (frequency: string) => {
    switch (frequency) {
      case 'immediate':
        return 'Immediate';
      case 'daily':
        return 'Daily Digest';
      case 'weekly':
        return 'Weekly Summary';
      default:
        return frequency;
    }
  };

  return (
    <SettingsCard
      title="Notification Settings"
      description="Configure how and when you receive notifications"
      icon={<Bell size={20} />}
      isEditing={isEditing}
      isSaving={isSaving}
      onEditToggle={handleEditToggle}
      onSave={handleSave}
    >
      {isEditing ? (
        <Stack spacing={3.5}>
          {/* Notification Channels */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
              Notification Channels
            </Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.emailNotifications}
                    onChange={() => handleToggleChange('emailNotifications')}
                  />
                }
                label={<Typography sx={{ fontSize: '0.875rem' }}>Email Notifications</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.smsNotifications}
                    onChange={() => handleToggleChange('smsNotifications')}
                  />
                }
                label={<Typography sx={{ fontSize: '0.875rem' }}>SMS Notifications</Typography>}
              />
            </Stack>
          </Box>

          {/* Notification Types */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
              Notification Types
            </Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.assessmentReminders}
                    onChange={() => handleToggleChange('assessmentReminders')}
                  />
                }
                label={<Typography sx={{ fontSize: '0.875rem' }}>Assessment Reminders</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.candidateUpdates}
                    onChange={() => handleToggleChange('candidateUpdates')}
                  />
                }
                label={<Typography sx={{ fontSize: '0.875rem' }}>Candidate Updates</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.systemAlerts}
                    onChange={() => handleToggleChange('systemAlerts')}
                  />
                }
                label={<Typography sx={{ fontSize: '0.875rem' }}>System Alerts</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.weeklyReports}
                    onChange={() => handleToggleChange('weeklyReports')}
                  />
                }
                label={<Typography sx={{ fontSize: '0.875rem' }}>Weekly Reports</Typography>}
              />
            </Stack>
          </Box>

          {/* Frequency Settings */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
              Frequency Settings
            </Typography>
            <FormControl fullWidth size="small" sx={{ maxWidth: { xs: '100%', sm: 300 } }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary', mb: 0.75 }}>
                Notification Frequency
              </Typography>
              <Select
                value={formData.notificationFrequency}
                onChange={(e) => handleInputChange('notificationFrequency', e.target.value)}
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="immediate">Immediate</MenuItem>
                <MenuItem value="daily">Daily Digest</MenuItem>
                <MenuItem value="weekly">Weekly Summary</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Quiet Hours */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
              Quiet Hours
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
              <TextField
                size="small"
                type="time"
                label="Start Time"
                value={formData.quietHoursStart}
                onChange={(e) => handleInputChange('quietHoursStart', e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{ sx: { borderRadius: '8px' } }}
              />
              <TextField
                size="small"
                type="time"
                label="End Time"
                value={formData.quietHoursEnd}
                onChange={(e) => handleInputChange('quietHoursEnd', e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{ sx: { borderRadius: '8px' } }}
              />
            </Box>
          </Box>
        </Stack>
      ) : (
        <Stack spacing={3}>
          {/* Notification Channels */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
              Notification Channels
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.75,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderRadius: '8px',
                  bgcolor: 'action.hover',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: formData.emailNotifications ? 'success.main' : 'text.disabled',
                    }}
                  />
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}>
                    Email Notifications
                  </Typography>
                </Stack>
                <Chip
                  size="small"
                  label={formData.emailNotifications ? 'Enabled' : 'Disabled'}
                  color={formData.emailNotifications ? 'success' : 'default'}
                  variant="outlined"
                />
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: 1.75,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderRadius: '8px',
                  bgcolor: 'action.hover',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: formData.smsNotifications ? 'success.main' : 'text.disabled',
                    }}
                  />
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}>
                    SMS Notifications
                  </Typography>
                </Stack>
                <Chip
                  size="small"
                  label={formData.smsNotifications ? 'Enabled' : 'Disabled'}
                  color={formData.smsNotifications ? 'success' : 'default'}
                  variant="outlined"
                />
              </Paper>
            </Box>
          </Box>

          {/* Active Notification Types */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
              Active Notification Types
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {[
                { key: 'assessmentReminders', label: 'Assessment Reminders' },
                { key: 'candidateUpdates', label: 'Candidate Updates' },
                { key: 'systemAlerts', label: 'System Alerts' },
                { key: 'weeklyReports', label: 'Weekly Reports' },
              ].map(({ key, label }) => {
                const isActive = Boolean(formData[key as keyof NotificationSettingsData]);
                return (
                  <Paper
                    key={key}
                    variant="outlined"
                    sx={{
                      p: 1.75,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderRadius: '8px',
                      bgcolor: 'action.hover',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: isActive ? 'success.main' : 'text.disabled',
                        }}
                      />
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}>
                        {label}
                      </Typography>
                    </Stack>
                    <Chip
                      size="small"
                      label={isActive ? 'On' : 'Off'}
                      color={isActive ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Paper>
                );
              })}
            </Box>
          </Box>

          {/* Frequency */}
          <Box>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: '8px',
                bgcolor: 'rgba(59, 130, 246, 0.04)',
                borderColor: 'rgba(59, 130, 246, 0.2)',
              }}
            >
              <DetailedItem
                label="Notification Frequency"
                text={getFrequencyDisplayText(formData.notificationFrequency)}
                helpertext="How often you receive notification summaries"
              />
            </Paper>
          </Box>

          {/* Quiet Hours */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
              Quiet Hours
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
                <DetailedItem label="Start Time" text={formData.quietHoursStart} />
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
                <DetailedItem label="End Time" text={formData.quietHoursEnd} />
              </Paper>
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1 }}>
              No notifications will be sent during quiet hours
            </Typography>
          </Box>
        </Stack>
      )}
    </SettingsCard>
  );
};
